import "./map.css";
import "ol-ext/dist/ol-ext.css";

import {Map, View} from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {fromLonLat, toLonLat} from "ol/proj";
import {XYZ} from "ol/source";
import {Hill, HillFile} from "./ts/Hill";
import {defaults as defaultControls, ScaleLine} from "ol/control";
import {GPXBuilder} from "./ts/GPXBuilder";
import {BaggingFile, Visit} from "./ts/Bag";
import {LayerDef} from "./ts/LayerDef";
import {MunroLayerDef} from "./ts/MunroLayerDef";
import {TopsLayerDef} from "./ts/TopsLayerDef";
import {CorbettsLayerDef} from "./ts/CorbettsLayerDef";
import {FionasLayerDef} from "./ts/FionasLayerDef";
import {MarilynLayerDef} from "./ts/MarilynLayerDef";
import {HumpsLayerDef} from "./ts/HumpsLayerDef";
import {SimmLayerDef} from "./ts/SimmLayerDef";
import {RemainingLayerDef} from "./ts/RemainingLayerDef";
import {loadStravaTraces} from "./ts/strava";
import {WainwrightsLayerDef} from "./ts/WainwrightsLayerDef";
import {HillLists} from "./ts/LayerDefBase";
import {OutlierLayerDef} from "./ts/OutlierLayerDef";
import {setupTabs} from "./tabs";
import LayerSwitcher from "ol-ext/control/LayerSwitcher";
import LayerGroup from "ol/layer/Group";

document.addEventListener("DOMContentLoaded", () => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop as string),
    });

    const rasterLayers: TileLayer<XYZ | OSM>[] = [];
    const localUrl = "http://localhost:8200/tile/{z}/{x}/{y}";
    const cycleUrl =
        "https://{a-c}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png";
    const zoomedInLayer = new TileLayer({
        minZoom: 13,
        properties: {
            title: "CyclOSM",
            displayInLayerSwitcher: false,
        },
        source: new XYZ({
            url: params["localTiles"] ? localUrl : cycleUrl,
        }),
    });
    rasterLayers.push(zoomedInLayer);
    const osmLayer = new TileLayer({
        properties: {
            title: "OSM",
            displayInLayerSwitcher: false,
        },
        maxZoom: 13,
        source: new OSM(),
    });
    rasterLayers.push(osmLayer);
    rasterLayers.forEach((r) => r.setOpacity(0.6));

    const munroLayerDef = new MunroLayerDef();
    const wainwrightsLayerDef = new WainwrightsLayerDef();
    const layerDefs: LayerDef[] = [
        new RemainingLayerDef(),
        new HumpsLayerDef(),
        new MarilynLayerDef(),
        new SimmLayerDef(),
        new OutlierLayerDef(),
        wainwrightsLayerDef,
        new FionasLayerDef(),
        new CorbettsLayerDef(),
        new TopsLayerDef(),
        munroLayerDef,
    ];

    const stravaSource = new VectorSource({wrapX: false});
    const stravaLayer = new VectorLayer({
        source: stravaSource,
        visible: true,
        properties: {displayInLayerSwitcher: false},
    });

    layerDefs.forEach((d) => {
        const vSource = new VectorSource({wrapX: false});
        d.setVisitedSource(vSource);

        const uSource = new VectorSource({wrapX: false});
        d.setUnvisitedSource(uSource);

        const visited = new VectorLayer({
            source: vSource,
            visible: d.getInitiallyVisible(),
            properties: {title: "Visited"},
        });

        const unvisited = new VectorLayer({
            source: uSource,
            visible: d.getInitiallyVisible(),
            properties: {title: "Unvisited"},
        });

        const group = new LayerGroup({
            layers: [unvisited, visited],
            visible: d.getInitiallyVisible(),
            properties: {
                title: d.getName(),
            },
        });

        group.on("change:visible", () => {
            const isVis = group.getVisible();
            visited.setVisible(isVis);
            unvisited.setVisible(isVis);
        });

        d.setGroup(group);
    });

    const mapView = new View({maxZoom: 19});
    mapView.setZoom(10);
    mapView.setCenter(fromLonLat([-2.98, 54.4804]));
    var map: Map;

    function getPeopleList(peopleArray) {
        if (!peopleArray || !peopleArray.length) {
            return "On my own";
        }
        const maxIdx = peopleArray.length - 1;
        return (
            "With " +
            peopleArray.reduce((acc, cur, idx) => {
                if (idx === 0) {
                    return acc + " " + cur;
                }
                if (idx === maxIdx) {
                    return acc + " & " + cur;
                }
                return acc + ", " + cur;
            }, "")
        );
    }

    const switcher = new LayerSwitcher({
        collapsed: true,
        reordering: false,
        mouseover: true,
        expanded: true,
    });

    function initialiseMap() {
        map = new Map({
            controls: defaultControls().extend([new ScaleLine(), switcher]),
            target: "map",
            layers: [
                ...rasterLayers,
                stravaLayer,
                ...layerDefs.map((d) => d.getGroup()),
            ],
            keyboardEventTarget: document,
            view: mapView,
        });

        map.on("click", function (e) {
            const feature = map.forEachFeatureAtPixel(e.pixel, (f) =>
                f.getProperties().hill ? f : false
            );
            if (!feature) {
                return;
            }

            document.getElementById("hill-details").style.display = "block";

            const properties = feature.getProperties();
            const hill = properties.hill as Hill;
            const visits = properties.visits as Visit[];

            document.getElementById("name").textContent = hill.name;
            document.getElementById("height").textContent = `${hill.elev}m`;
            document.getElementById("drop").textContent = `${hill.drop}m`;
            document.getElementById("id").textContent = String(hill.id);
            document.getElementById("visit-count").textContent =
                properties.visits.length;
            document.getElementById("categories").innerHTML = hill.categories
                .map(
                    (c) =>
                        `<div class="label label-${c.toLowerCase()}">${c}</div>`
                )
                .join("");

            loadStravaTraces(hill.id, visits, stravaSource);

            let visitDetails = document.getElementById("visit-details");
            visitDetails.innerHTML = "";
            visits.forEach((v: Visit) => {
                const visit = document.createElement("div");
                visit.classList.add("visit");

                const visitDate = document.createElement("div");
                const d = new Date(v.date);
                visitDate.textContent = d.toLocaleDateString(undefined, {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                });
                visit.append(visitDate);

                if (v.with?.length) {
                    const who = document.createElement("div");
                    who.textContent = getPeopleList(v.with);
                    visit.append(who);
                }

                const notes = document.createElement("div");
                notes.textContent = v.notes;
                visit.append(notes);

                if (v.strava) {
                    const stravaDiv = document.createElement("div");
                    const stravaLink = document.createElement("a");
                    stravaLink.href = `https://www.strava.com/activities/${v.strava}`;
                    stravaLink.textContent = "Strava";
                    stravaLink.target = "_blank";
                    stravaDiv.append(stravaLink);
                    visit.append(stravaDiv);
                }

                visitDetails.append(visit);
            });
            document.getElementById("visit-container").style.display =
                properties.visits.length ? "block" : "none";

            if (isNarrow()) {
                document.getElementById("rightPanel").style.display = "inherit";
            }
        });
    }

    let gpxBuilder: GPXBuilder;

    function updateURL(): void {
        const cen = toLonLat(mapView.getCenter());
        const z = Math.floor(mapView.getZoom());
        window.location.hash =
            "#map=" + z + "/" + cen[1].toFixed(4) + "/" + cen[0].toFixed(4);
    }

    function setViewFromHash(): boolean {
        let hash = window.location.hash;
        hash = hash.substring(1);
        let hashMap = new Map();

        hash.split("&").forEach((s) => {
            const parts = s.split("=");
            if (parts.length == 2) {
                hashMap.set(parts[0], parts[1]);
            }
        });

        let mapParams = hashMap.get("map");
        if (!mapParams) {
            return false;
        }

        let mapParts = mapParams.split("/");
        if (mapParts.length != 3) {
            return false;
        }

        const zoom = parseInt(mapParts[0], 10);
        const lon = parseFloat(mapParts[2]);
        const lat = parseFloat(mapParts[1]);

        mapView.setZoom(zoom);
        mapView.setCenter(fromLonLat([lon, lat]));
        return true;
    }

    function loadHillData(): Promise<Hill[]> {
        return fetch("hills-main.json")
            .then((r) => r.json())
            .then((hillFile: HillFile) => hillFile.hills);
    }

    function loadVisitData(): Promise<Visit[]> {
        return fetch("bagging.json")
            .then((r) => r.json())
            .then((bagging: BaggingFile) => bagging.bags);
    }

    function loadLakesHillData(): Promise<Hill[]> {
        return fetch("hills-lakes.json")
            .then((r) => r.json())
            .then((hillFile: HillFile) => hillFile.hills);
    }

    function loadOtherHillData(): Promise<Hill[]> {
        const promises: Promise<Hill[]>[] = [];

        promises.push(
            fetch("hills-secondary.json")
                .then((r) => r.json())
                .then((hillFile: HillFile) => hillFile.hills)
        );

        promises.push(
            fetch("hills-remaining.json")
                .then((r) => r.json())
                .then((hillFile: HillFile) => hillFile.hills)
        );

        return Promise.all(promises).then((results) => {
            return results.flat(1);
        });
    }

    function loadHills() {
        Promise.all([
            loadHillData(),
            loadVisitData(),
            loadOtherHillData(),
            loadLakesHillData(),
        ])
            .then(([hills, visits, others, lakes]) => {
                const hillList: HillLists = {
                    main: hills,
                    others: others,
                    lakes: lakes,
                };
                layerDefs.forEach((def) => {
                    def.render(hillList, visits);
                    def.setProgressBar();
                    // def.setupGPX();
                });

                if (!setViewFromHash()) {
                    mapView.fit(munroLayerDef.getVisitedSource().getExtent(), {
                        padding: [20, 20, 20, 20],
                    });
                }

                gpxBuilder = new GPXBuilder(hills, visits);

                map.on("moveend", updateURL);
            })
            .catch((e) => {
                alert(e);
            });
    }

    initialiseMap();
    loadHills();

    document.getElementById("opacity").addEventListener("change", (event) => {
        const opacity = Number((event.currentTarget as any).value) / 100;
        rasterLayers.forEach((l) => l.setOpacity(opacity));
    });

    document.getElementById("gpx").addEventListener("click", () => {
        if (gpxBuilder) {
            const gpxString = gpxBuilder.build();
            const data = new Blob([gpxString], {type: "text/plain"});
            const url = window.URL.createObjectURL(data);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = "munros.gpx";
            document.body.appendChild(a);
            a.click();
        }
    });

    if (params["all"]) {
        layerDefs.forEach((ld) => {
            ld.getGroup()
                .getLayers()
                .forEach((l) => {
                    l.setVisible(true);
                });
            ld.getGroup().setVisible(true);
        });
    }
    if (params["ld"]) {
        wainwrightsLayerDef.getGroup().setVisible(true);
        wainwrightsLayerDef
            .getGroup()
            .getLayers()
            .forEach((l) => {
                l.setVisible(true);
            });
    }

    document.getElementById("close").onclick = () => {
        document.getElementById("rightPanel").style.display = "none";
    };

    setupTabs();
});

function isNarrow() {
    return window.innerWidth < 639;
}
